import type { OverlayTemplate } from 'klinecharts';

// ── Public data shape stored in overlay.extendData ───────────────────────────
export interface TextBoxData {
  text: string;
  fontSize: number;
  fontFamily: string;
  bold: boolean;
  italic: boolean;
  textColor: string;
  bgColor: string;
  bgOpacity: number;
  borderColor: string;
  borderWidth: number;
}

export const TEXT_BOX_DEFAULT: TextBoxData = {
  text: 'Text',
  fontSize: 14,
  fontFamily: 'sans-serif',
  bold: false,
  italic: false,
  textColor: '#ffffff',
  bgColor: '#2196F3',
  bgOpacity: 85,
  borderColor: '#1565C0',
  borderWidth: 1,
};

function hexToRgba(hex: string, opacity: number): string {
  if (!hex || hex.length < 7) return `rgba(33,150,243,${(opacity / 100).toFixed(2)})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${(opacity / 100).toFixed(2)})`;
}

function measureLineWidth(line: string, fontSize: number, fontFamily: string, bold: boolean, italic: boolean): number {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no ctx');
    const wt = italic && bold ? 'italic bold' : italic ? 'italic' : bold ? 'bold' : 'normal';
    ctx.font = `${wt} ${fontSize}px ${fontFamily}`;
    return ctx.measureText(line || ' ').width;
  } catch {
    return (line.length || 1) * fontSize * 0.65;
  }
}


function resolveData(extendData: unknown): TextBoxData {
  return {
    ...TEXT_BOX_DEFAULT,
    ...(typeof extendData === 'object' && extendData !== null
      ? (extendData as Partial<TextBoxData>)
      : {}),
  };
}

const textBox: OverlayTemplate = {
  name: 'textBox',
  totalStep: 2,
  needDefaultPointFigure: false,
  needDefaultXAxisFigure: false,
  needDefaultYAxisFigure: false,
  createPointFigures: ({ overlay, coordinates }) => {
    if (coordinates.length < 1) return [];

    const d = resolveData(overlay.extendData);

    const paddingX = 10;
    const paddingY = 8;
    const lineHeight = Math.ceil(d.fontSize * 1.5);

    const lines = (d.text || 'Text').split('\n');

    const weight =
      d.italic && d.bold ? 'italic bold' :
      d.italic           ? 'italic'      :
      d.bold             ? 'bold'        : 'normal';

    const boxW = Math.max(60, Math.max(...lines.map((l) => measureLineWidth(l, d.fontSize, d.fontFamily, d.bold, d.italic))) + paddingX * 2);
    const boxH = lines.length * lineHeight + paddingY * 2;

    const x = coordinates[0].x;
    const y = coordinates[0].y;

    return [
      // Background rect with border
      {
        type: 'rect',
        attrs: { x, y, width: boxW, height: boxH },
        styles: {
          style: 'stroke_fill',
          color: hexToRgba(d.bgColor, d.bgOpacity),
          borderColor: d.borderColor,
          borderSize: d.borderWidth,
          borderStyle: 'solid',
        },
      },
      // One text figure per line
      ...lines.map((line, i) => ({
        type: 'text',
        ignoreEvent: true,
        attrs: {
          x: x + paddingX,
          // Centre of each line slot → canvas baseline at middle of glyph
          y: y + paddingY + lineHeight * i + lineHeight / 2,
          text: line || ' ',
          baseline: 'middle',
        },
        styles: {
          style: 'fill',
          color: d.textColor,
          size: d.fontSize,
          family: d.fontFamily,
          weight,
          backgroundColor: 'transparent',
        },
      })),
    ];
  },
};

export default textBox;
